import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, type MenuProps } from 'antd';
import type { BlockedSlotModalRef } from 'pos-client/components/scheduleBoard/components/modals/BlockedSlotModal.tsx';
import BlockedSlotModal from 'pos-client/components/scheduleBoard/components/modals/BlockedSlotModal.tsx';
import { useAuthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useAuthorizeBlockedSlot.ts';
import { useDeleteBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useDeleteBlockedSlot.ts';
import { useUnauthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useUnauthorizeBlockSlot.ts';
import { confirmBlockedSlotDelete } from 'pos-client/components/scheduleBoard/utils/BlockedSlotUtils.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import type { ScheduleBoardBlockedSlotModel } from 'pos-shared/models/schedulingSchema.ts';

interface WorkshopBlockedSlotActionsProps {
  blockedSlot: ScheduleBoardBlockedSlotModel;
}

export const WorkshopBlockedSlotActions = ({
  blockedSlot,
}: WorkshopBlockedSlotActionsProps) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const blockedSlotModalRef = useRef<BlockedSlotModalRef | null>(null);
  const isAuthorised = blockedSlot.state.toLowerCase() === 'authorized';
  const canApproveBlockedSlots = hasPermissions('authorize.blocked.slot');
  const canEditBlockedSlots =
    hasPermissions('edit.authorized.blocked.slot') ||
    hasPermissions('edit.unauthorized.blocked.slot');
  const { authorizeBlockedSlot } = useAuthorizeBlockedSlot();
  const { deleteBlockedSlot } = useDeleteBlockedSlot();
  const { unauthorizeBlockedSlot } = useUnauthorizeBlockedSlot();

  const handleBlockedSlotDelete = useCallback(
    (blockedSlotId: string) => {
      confirmBlockedSlotDelete(() => deleteBlockedSlot(blockedSlotId));
    },
    [deleteBlockedSlot]
  );

  const handleEditBlockedSlot = useCallback(
    (slotData: ScheduleBoardBlockedSlotModel) => {
      blockedSlotModalRef.current?.openEditModal({
        startDateTime: slotData.startDate as string,
        finishDateTime: slotData.endDate as string,
        blockedSlotId: slotData.id || '',
        assetId: slotData.laneId,
        description: slotData.description ?? '',
      });
    },
    []
  );

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        label: 'Authorize',
        key: 'authorize',
        hidden: !canApproveBlockedSlots || isAuthorised,
        onClick: () =>
          authorizeBlockedSlot({
            blockedSlotId: blockedSlot.id,
          }),
      },
      {
        label: 'Unauthorize',
        key: 'unauthorize',
        hidden: !canApproveBlockedSlots || !isAuthorised,
        onClick: () =>
          unauthorizeBlockedSlot({
            blockedSlotId: blockedSlot.id,
          }),
      },
      {
        label: 'Edit',
        key: 'edit',
        onClick: () => handleEditBlockedSlot(blockedSlot),
        hidden: !canEditBlockedSlots,
      },
      {
        label: 'Delete',
        key: 'delete',
        onClick: () => handleBlockedSlotDelete(blockedSlot.id),
        hidden: !canEditBlockedSlots,
      },
    ],
    [
      authorizeBlockedSlot,
      isAuthorised,
      handleBlockedSlotDelete,
      unauthorizeBlockedSlot,
      handleEditBlockedSlot,
      blockedSlot,
      canApproveBlockedSlots,
      canEditBlockedSlots,
    ]
  );

  return (
    <>
      <BlockedSlotModal ref={blockedSlotModalRef} />
      <Dropdown menu={{ items: menuItems }}>
        <Button size="small" icon={<FontAwesomeIcon icon={faChevronDown} />}>
          {t('common.buttons.actions')}
        </Button>
      </Dropdown>
    </>
  );
};
